import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import * as dot from 'ts-dot-prop';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Address, SortOrder } from '../../../../generated/generated-types';
import { FormlyFillHeightListHostDirective } from '../../../custom-formly/formly-tabs/formly-fill-height-list-host.directive';
import { DocumentLineItemService } from '../../services/document-line-item.service';
import { DocumentService } from '../../services/document.service';
import { NewWidgetDocumentLineItemComponent } from '../new-widget-document-line-item/new-widget-document-line-item.component';

import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-formly-document-line-item-list',
  templateUrl: './formly-document-line-item-list.component.html',
  hostDirectives: [FormlyFillHeightListHostDirective],
  host: {
    '[class.phx-base-list--content-height]': 'isContentHeightList',
  },
  standalone: true,
  imports: [
    NewWidgetDocumentLineItemComponent,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    BlockableDiv,
  ]
})
export class FormlyDocumentLineItemListComponent extends FieldType implements OnInit, AfterViewInit {

  /** Bound to blockable-div `class` input (inner content div — not the host). */
  get contentFlexClass(): string {
    return this.isContentHeightList
      ? 'w-full'
      : 'flex min-h-0 flex-1 flex-col w-full overflow-hidden';
  }

  /** True when the tab opts out of fill-height (detail `.content` scrolls instead). */
  get isContentHeightList(): boolean {
    return this.props?.fillHeight === false;
  }

  /** When `props.fillHeight === false`, list sizes to rows; detail `.content` scrolls. */
  get fillHeightList(): boolean {
    return !this.isContentHeightList;
  }

  /** Product DLI tab uses product copy; project uses project copy; address keeps address copy. */
  get saveFirstLabelKey(): string {
    if (this.props?.productIdFilterField) {
      return 'product.save-product-first';
    }
    if (this.props?.projectIdFilterField) {
      return 'document.project.save-project-first';
    }
    return 'address.save-address-first';
  }

  get missingDataLabelKey(): string {
    if (this.props?.productIdFilterField) {
      return 'product.missing-data-for-save';
    }
    if (this.props?.projectIdFilterField) {
      return 'document.project.missing-data-for-save';
    }
    return 'address.missing-data-for-save';
  }

  formConfigIdentifier = "document-line-item-list";

  public fields: FormlyFieldConfig[] = [];
  public detailForm: UntypedFormGroup;
  sortOrder: SortOrder = SortOrder.DESC

  @ViewChild(NewWidgetDocumentLineItemComponent)
  lineItemsWidget: NewWidgetDocumentLineItemComponent;
  isSaving = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private i18n: I18nService,
    notificationService: NotificationService,
    private ngxUiLoader: NgxUiLoaderService,
    private cdRef: ChangeDetectorRef,
    private baseListServices: BaseListCombinedService,
    private documentService: DocumentService,
    private dliService: DocumentLineItemService) {
    super();
  }

  async ngOnInit() {
    //let res = await this.baseListServices.formService.getAutoFormConfig(this.formConfigIdentifier).single$.toPromise();

    // formControl valueChanges was debug-only and leaked; filter refresh is driven by form.valueChanges below.

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildFilterAndRefreshIfNeeded();
    });
  }

  async ngAfterViewInit() {
    this.buildFilterAndRefreshIfNeeded();
  }

  async buildFilterAndRefreshIfNeeded() {
    let refreshNeeded = false;

    if (this.props.addressIdFilterField && this.lineItemsWidget) {
      const addressId = dot.get(this.model, this.props.addressIdFilterField);
      if (addressId != this.lineItemsWidget.addressId) {
        this.lineItemsWidget.addressId.set(addressId);
        refreshNeeded = true;
      }
    }

    if (this.props.productIdFilterField && this.lineItemsWidget) {
      const productId = dot.get(this.model, this.props.productIdFilterField);
      if (productId != this.lineItemsWidget.productId) {
        this.lineItemsWidget.productId.set(productId);
        refreshNeeded = true;
      }
    }

    if (this.props.projectIdFilterField && this.lineItemsWidget) {
      const projectId = dot.get(this.model, this.props.projectIdFilterField);
      if (projectId != this.lineItemsWidget.projectId) {
        this.lineItemsWidget.projectId.set(projectId);
        refreshNeeded = true;
      }
    }

    if (refreshNeeded) {
      this.lineItemsWidget.refresh();
    }
  }

  checkIfCanSave(): boolean {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode(): Promise<void> {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    if (!component) {
      console.error("No detail component found to save AddressPostalLink");
      return;
    }

    this.isSaving = true;
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving = false;
    this.cdRef.detectChanges();
  }
}
